import React, { useEffect, useMemo, useState } from 'react';
import type { Company, FoundationPayload, Filters, CashflowPayload, LoansPayload } from '../types/biApp';
import { quickFilter } from '../shared/buildQueryParams';
import { formatDate, parseDateInputToIso } from '../shared/formatting';
import { DateInput } from '../shared/DateInput';
import { SHELL_MODULE_REGISTRY, shellNavLabel, type ModuleKey } from '../shared/biModuleRegistry';
import { canOpenModule, hasModuleGrant } from '../shared/biModuleKeys';
import { canOpenAdmin, defaultAdoCompanyIds, normalizeNumberArray, previousQuarterRangeIso } from '../shared/foundationFilters';

function companyScopeTitle(company: Company): string {
    const lines: string[] = [company.legal_name];
    if (company.oib) {
        lines.push(`OIB ${company.oib}`);
    }
    if (company.mbs) {
        lines.push(`MBS ${company.mbs}`);
    }
    const address = [company.street_address, [company.postal_code, company.city].filter(Boolean).join(' ')].filter(Boolean);
    if (address.length) {
        lines.push(address.join(', '));
    }
    if (company.phone) {
        lines.push(company.phone);
    }
    if (company.email) {
        lines.push(company.email);
    }
    if (company.website) {
        lines.push(company.website);
    }

    return lines.join('\n');
}

function ScopeDateField({
    label,
    isoValue,
    onCommitIso,
    locale,
}: {
    label: string;
    isoValue: string;
    onCommitIso: (iso: string) => void;
    locale: 'hr' | 'en';
}) {
    const [draft, setDraft] = useState(() => {
        if (locale !== 'hr' || !isoValue || !/^\d{4}-\d{2}-\d{2}$/.test(isoValue)) {
            return '';
        }

        return formatDate(isoValue, 'hr');
    });

    useEffect(() => {
        if (locale !== 'hr') {
            return;
        }
        if (isoValue && /^\d{4}-\d{2}-\d{2}$/.test(isoValue)) {
            setDraft(formatDate(isoValue, 'hr'));
        } else {
            setDraft('');
        }
    }, [isoValue, locale]);

    if (locale === 'en') {
        return (
            <label className="bi-field bi-field--compact">
                <span className="bi-field__label">{label}</span>
                <DateInput className="bi-input" value={isoValue} onChange={onCommitIso} />
            </label>
        );
    }

    return (
        <label className="bi-field bi-field--compact">
            <span className="bi-field__label">{label}</span>
            <input
                className="bi-input"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="dd.mm.gggg"
                spellCheck={false}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={() => {
                    const parsed = parseDateInputToIso(draft);
                    if (parsed) {
                        onCommitIso(parsed);
                    } else if (isoValue && /^\d{4}-\d{2}-\d{2}$/.test(isoValue)) {
                        setDraft(formatDate(isoValue, 'hr'));
                    }
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        (event.target as HTMLInputElement).blur();
                    }
                }}
            />
        </label>
    );
}

type DraftSlice = Pick<Filters, 'companyIds' | 'dateFrom' | 'dateTo' | 'accountId' | 'counterpartyId' | 'loanCompanyId'>;

function periodSelectValue(draft: DraftSlice, foundation: FoundationPayload): string {
    for (const preset of foundation.ui.quick_filters) {
        if (preset === 'raspon od-do') {
            continue;
        }
        const q = quickFilter(preset);
        if (q.dateFrom === draft.dateFrom && q.dateTo === draft.dateTo) {
            return preset;
        }
    }

    return '__custom__';
}

function sameCompanyIds(a: number[], b: number[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    const sa = [...a].sort((x, y) => x - y);
    const sb = [...b].sort((x, y) => x - y);

    return sa.every((id, i) => id === sb[i]);
}

function draftMatchesFilters(draft: DraftSlice, filters: Filters): boolean {
    return (
        draft.dateFrom === filters.dateFrom
        && draft.dateTo === filters.dateTo
        && draft.accountId === filters.accountId
        && draft.counterpartyId === filters.counterpartyId
        && draft.loanCompanyId === filters.loanCompanyId
        && sameCompanyIds(draft.companyIds, filters.companyIds)
    );
}

export function ModuleFilters({
    foundation,
    filters,
    onChange,
    showAccount,
    cashflow,
    showCounterparty,
    loans,
    lockToAdoOnly = false,
    locale,
    moduleNav,
    showGlobalFilters = true,
}: {
    foundation: FoundationPayload;
    filters: Filters;
    onChange: (patch: Partial<Filters>) => void;
    showAccount: boolean;
    cashflow: CashflowPayload | null;
    showCounterparty: boolean;
    loans: LoansPayload | null;
    lockToAdoOnly?: boolean;
    locale: 'hr' | 'en';
    /** Kad je false (npr. admin, Gold CM): samo padajući odabir modula, bez tvrtki/perioda. */
    showGlobalFilters?: boolean;
    moduleNav: {
        activeModule: ModuleKey;
        onNavigate: (key: ModuleKey) => void;
        user: { role: string; allowed_modules?: string[]; email?: string };
    };
}) {
    const [draft, setDraft] = useState<DraftSlice>(() => ({
        companyIds: [...filters.companyIds],
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        accountId: filters.accountId,
        counterpartyId: filters.counterpartyId,
        loanCompanyId: filters.loanCompanyId,
    }));

    const [periodPreferCustom, setPeriodPreferCustom] = useState(
        () => periodSelectValue(
            {
                companyIds: [...filters.companyIds],
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                accountId: filters.accountId,
                counterpartyId: filters.counterpartyId,
                loanCompanyId: filters.loanCompanyId,
            },
            foundation,
        ) === '__custom__',
    );

    useEffect(() => {
        setDraft({
            companyIds: [...filters.companyIds],
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            accountId: filters.accountId,
            counterpartyId: filters.counterpartyId,
            loanCompanyId: filters.loanCompanyId,
        });
        setPeriodPreferCustom(periodSelectValue(
            {
                companyIds: filters.companyIds,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                accountId: filters.accountId,
                counterpartyId: filters.counterpartyId,
                loanCompanyId: filters.loanCompanyId,
            },
            foundation,
        ) === '__custom__');
    }, [filters, foundation]);

    const visibleNavEntries = useMemo(
        () => SHELL_MODULE_REGISTRY.filter((entry) => {
            // Owner-only moduli (Knjigovodstvo, CFO Radni popis): u MODUL izborniku samo vlasniku.
            if (entry.key === 'knjigovodstvo' || entry.key === 'cfo_tasks') {
                return (moduleNav.user.email ?? '').toLowerCase() === 'vjakomin@gmail.com';
            }
            const canOpenThis = entry.key === 'admin'
                ? (canOpenAdmin(moduleNav.user) && canOpenModule(moduleNav.user, 'admin'))
                : canOpenModule(moduleNav.user, entry.key);

            if (entry.hiddenFromNav) {
                // Aktivni modul UVIJEK u listi — inače controlled <select value=...> nema opciju
                // i renderira PRAZNO (npr. poslovnica u 40.3 kad je parent 40 navigabilan preko fallbacka).
                if (entry.key === moduleNav.activeModule) return true;
                if (!entry.parentKey) return false;
                // RAW grant na sub (ne canOpenModule): always-true specijali (gap/pentest) inače
                // procure owner-only stavke svima bez parent granta.
                if (!hasModuleGrant(moduleNav.user, entry.key)) return false;
                // Parent navigabilan (direktno ILI preko child-fallbacka) → sub skriven; do njega
                // se dolazi kroz parent landing (auto-jump). platni_cf faze ostaju skrivene superadminu.
                return !canOpenModule(moduleNav.user, entry.parentKey);
            }

            return canOpenThis;
        }),
        [moduleNav.user, moduleNav.activeModule],
    );

    const visibleCompanies = lockToAdoOnly
        ? foundation.companies.filter((company) => company.code.toUpperCase() === 'ADO')
        : foundation.companies;

    const resolvedPeriod = periodSelectValue(draft, foundation);
    const periodSelectUiValue = periodPreferCustom || resolvedPeriod === '__custom__' ? '__custom__' : resolvedPeriod;
    const showCustomDates = periodPreferCustom || resolvedPeriod === '__custom__';
    const scopeCount = draft.companyIds.length;
    const applyDisabled = draftMatchesFilters(draft, filters);

    const activeSummary = useMemo(() => {
        const moduleEntry = SHELL_MODULE_REGISTRY.find((e) => e.key === moduleNav.activeModule);
        const moduleLabel = moduleEntry ? shellNavLabel(moduleEntry, locale) : moduleNav.activeModule;

        let companiesLabel: string;
        if (lockToAdoOnly) {
            companiesLabel = 'ADO';
        } else {
            const codes = draft.companyIds
                .map((id) => visibleCompanies.find((c) => c.id === id)?.code)
                .filter(Boolean) as string[];
            if (codes.length === 0) {
                companiesLabel = locale === 'en' ? 'No companies' : 'Nema tvrtki';
            } else if (codes.length === 1) {
                companiesLabel = codes[0];
            } else {
                companiesLabel = `${codes[0]} + ${codes.length - 1}`;
            }
        }

        let periodLabel: string;
        if (!periodPreferCustom && resolvedPeriod !== '__custom__') {
            periodLabel = resolvedPeriod;
        } else if (draft.dateFrom && draft.dateTo) {
            periodLabel = `${formatDate(draft.dateFrom, locale)}–${formatDate(draft.dateTo, locale)}`;
        } else {
            periodLabel = '—';
        }

        const extras: string[] = [];
        if (showAccount && draft.accountId && cashflow) {
            const acc = cashflow.accounts.find((a) => String(a.id) === draft.accountId);
            if (acc) {
                extras.push(`${acc.company_code} · ${acc.iban}`);
            }
        }
        if (showCounterparty && draft.counterpartyId && loans) {
            const party = loans.counterparties.find((p) => String(p.id) === draft.counterpartyId);
            if (party) {
                extras.push(party.name);
            }
        }

        return { moduleLabel, companiesLabel, periodLabel, extras };
    }, [
        moduleNav.activeModule,
        locale,
        lockToAdoOnly,
        draft.companyIds,
        draft.dateFrom,
        draft.dateTo,
        periodPreferCustom,
        resolvedPeriod,
        visibleCompanies,
        showAccount,
        draft.accountId,
        cashflow,
        showCounterparty,
        draft.counterpartyId,
        loans,
    ]);

    function applyDraft() {
        onChange({
            companyIds: draft.companyIds,
            dateFrom: draft.dateFrom,
            dateTo: draft.dateTo,
            accountId: draft.accountId,
            counterpartyId: draft.counterpartyId,
            loanCompanyId: draft.loanCompanyId,
            page: 1,
        });
    }

    function resetDraftAndApply() {
        const range = previousQuarterRangeIso();
        const companyIds = lockToAdoOnly
            ? defaultAdoCompanyIds(foundation)
            : normalizeNumberArray(foundation.companies.map((c) => c.id));
        const next: DraftSlice = {
            companyIds,
            dateFrom: range.dateFrom,
            dateTo: range.dateTo,
            accountId: '',
            counterpartyId: '',
            loanCompanyId: '',
        };
        setDraft(next);
        onChange({
            ...next,
            page: 1,
        });
    }

    function toggleCompany(companyId: number) {
        if (lockToAdoOnly) {
            return;
        }
        setDraft((d) => {
            const companyIds = d.companyIds.includes(companyId)
                ? d.companyIds.filter((id) => id !== companyId)
                : [...d.companyIds, companyId];

            return { ...d, companyIds, counterpartyId: '', loanCompanyId: '' };
        });
    }

    const periodPresets = foundation.ui.quick_filters.filter((f) => f !== 'raspon od-do');

    return (
        <section className="bi-scope bi-scope--compact" aria-label={locale === 'en' ? 'Scope' : 'Područje'}>
            <div className="bi-scope-compact__toolbar">
                <label className="bi-field bi-field--compact bi-field--module">
                    <span className="bi-field__label">{locale === 'en' ? 'Section' : 'Modul'}</span>
                    <select
                        className="bi-input"
                        value={moduleNav.activeModule}
                        onChange={(event) => moduleNav.onNavigate(event.target.value as ModuleKey)}
                    >
                        {visibleNavEntries.map((entry) => (
                            <option value={entry.key} key={entry.key}>
                                {shellNavLabel(entry, locale)}
                            </option>
                        ))}
                    </select>
                </label>

                {lockToAdoOnly && showGlobalFilters ? (
                    <div className="bi-scope-locked">
                        <span className="bi-field__label">{locale === 'en' ? 'Scope' : 'Tvrtke'}</span>
                        <span className="bi-scope-locked__value" title={locale === 'en' ? 'Module locked to ADO.' : 'Modul je zaključan na ADO.'}>
                            ADO
                        </span>
                    </div>
                ) : showGlobalFilters ? (
                    <details className="bi-scope-dd">
                        <summary className="bi-scope-dd__summary">
                            <span className="bi-scope-dd__label">{locale === 'en' ? 'Scope' : 'Tvrtke'}</span>
                            <span className="bi-scope-badge" aria-label={locale === 'en' ? 'Selected companies' : 'Odabrane tvrtke'}>
                                {scopeCount}
                            </span>
                            <span className="bi-scope-dd__caret" aria-hidden>▾</span>
                        </summary>
                        <div className="bi-scope-dd__slot">
                            <div className="bi-scope-dd__panel">
                                <div className="bi-scope-dd__head">
                                    <span className="bi-scope-dd__title">{locale === 'en' ? 'Companies' : 'Tvrtke'}</span>
                                    {visibleCompanies.length > 1 ? (
                                        <button
                                            type="button"
                                            className="bi-link"
                                            onClick={() => {
                                                setDraft((d) => ({
                                                    ...d,
                                                    companyIds: [],
                                                    counterpartyId: '',
                                                    loanCompanyId: '',
                                                }));
                                            }}
                                        >
                                            {locale === 'en' ? 'Clear' : 'Očisti'}
                                        </button>
                                    ) : null}
                                </div>
                                <ul className="bi-scope-dd__list">
                                    {visibleCompanies.map((company) => {
                                        const active = draft.companyIds.includes(company.id);

                                        return (
                                            <li key={company.id}>
                                                <label className="bi-scope-dd__item">
                                                    <input
                                                        type="checkbox"
                                                        checked={active}
                                                        onChange={() => toggleCompany(company.id)}
                                                        title={companyScopeTitle(company)}
                                                    />
                                                    <span>{company.code}</span>
                                                </label>
                                            </li>
                                        );
                                    })}
                                </ul>
                                {visibleCompanies.length > 1 ? (
                                    <div className="bi-scope-dd__foot">
                                        <button
                                            type="button"
                                            className="bi-link"
                                            onClick={() => {
                                                setDraft((d) => ({
                                                    ...d,
                                                    companyIds: visibleCompanies.map((c) => c.id),
                                                    counterpartyId: '',
                                                    loanCompanyId: '',
                                                }));
                                            }}
                                        >
                                            {locale === 'en' ? 'Select all' : 'Odaberi sve'}
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </details>
                ) : null}

                {showGlobalFilters ? (
                    <>
                <label className="bi-field bi-field--compact">
                    <span className="bi-field__label">{locale === 'en' ? 'Period' : 'Period'}</span>
                    <select
                        className={`bi-input ${showCustomDates ? 'bi-input--accent' : ''}`}
                        value={periodSelectUiValue}
                        onChange={(event) => {
                            const value = event.target.value;
                            if (value === '__custom__') {
                                setPeriodPreferCustom(true);

                                return;
                            }
                            setPeriodPreferCustom(false);
                            setDraft((d) => ({ ...d, ...quickFilter(value) }));
                        }}
                    >
                        {periodPresets.map((preset) => (
                            <option value={preset} key={preset}>
                                {preset}
                            </option>
                        ))}
                        <option value="__custom__">{locale === 'en' ? 'Custom range' : 'Prilagođeno'}</option>
                    </select>
                </label>

                {showCustomDates ? (
                    <>
                        <ScopeDateField
                            label={locale === 'en' ? 'From' : 'Od'}
                            isoValue={draft.dateFrom}
                            locale={locale}
                            onCommitIso={(iso) => setDraft((d) => ({ ...d, dateFrom: iso }))}
                        />
                        <ScopeDateField
                            label={locale === 'en' ? 'To' : 'Do'}
                            isoValue={draft.dateTo}
                            locale={locale}
                            onCommitIso={(iso) => setDraft((d) => ({ ...d, dateTo: iso }))}
                        />
                    </>
                ) : null}

                {showAccount ? (
                    <label className="bi-field bi-field--compact">
                        <span className="bi-field__label">{locale === 'en' ? 'Account' : 'Račun'}</span>
                        <select
                            className="bi-input"
                            value={draft.accountId}
                            onChange={(event) => setDraft((d) => ({ ...d, accountId: event.target.value }))}
                        >
                            <option value="">{locale === 'en' ? 'All accounts' : 'Svi računi'}</option>
                            {cashflow?.accounts.map((account) => (
                                <option value={account.id} key={account.id}>{account.company_code} · {account.iban}</option>
                            ))}
                        </select>
                    </label>
                ) : null}

                {showCounterparty ? (
                    <label className="bi-field bi-field--compact">
                        <span className="bi-field__label">{locale === 'en' ? 'Counterparty' : 'Protustranka'}</span>
                        <select
                            className="bi-input"
                            value={draft.counterpartyId}
                            onChange={(event) => setDraft((d) => ({ ...d, counterpartyId: event.target.value }))}
                        >
                            <option value="">{locale === 'en' ? 'All counterparties' : 'Sve protustranke'}</option>
                            {loans?.counterparties.map((party) => (
                                <option value={party.id ?? ''} key={`${party.id}-${party.name}`}>{party.name}</option>
                            ))}
                        </select>
                    </label>
                ) : null}

                <div className="bi-scope-compact__actions">
                    <button type="button" className="bi-link bi-link--quiet bi-scope-compact__reset" onClick={resetDraftAndApply}>
                        <span className="bi-scope-compact__reset-icon" aria-hidden>↻</span>
                        {locale === 'en' ? 'Reset' : 'Reset'}
                    </button>
                    <button type="button" className="compact" disabled={applyDisabled} onClick={applyDraft}>
                        {locale === 'en' ? 'Apply' : 'Primijeni'}
                    </button>
                </div>
                    </>
                ) : null}
            </div>

            <div className="bi-scope-compact__active" aria-live="polite">
                <span className="bi-scope-compact__active-label">{locale === 'en' ? 'Active:' : 'Aktivno:'}</span>
                <span className="bi-scope-compact__chip">{activeSummary.moduleLabel}</span>
                {showGlobalFilters ? (
                    <>
                        <span className="bi-scope-compact__sep">·</span>
                        <span className="bi-scope-compact__chip">{activeSummary.companiesLabel}</span>
                        <span className="bi-scope-compact__sep">·</span>
                        <span className="bi-scope-compact__chip">{activeSummary.periodLabel}</span>
                        {activeSummary.extras.map((text) => (
                            <React.Fragment key={text}>
                                <span className="bi-scope-compact__sep">·</span>
                                <span className="bi-scope-compact__chip">{text}</span>
                            </React.Fragment>
                        ))}
                    </>
                ) : null}
            </div>
        </section>
    );
}
